import React, { useEffect, useState } from 'react';
import { DatabaseSoundPackModel, PackInfoStyle } from '../../services/DatabaseSoundPack';
import { DatabaseUpcomingPack, DatabaseUpcomingPackModel } from '../../services/DatabaseUpcomingPack';
import { useSoundPacks } from '../../context/SoundPacksContext';
import { formatShortDate } from '../../helpers/formatShortDate';
import { DatabaseSoundPackSoundsModel, DatabaseSound } from '../../services/DatabaseSoundPackSounds';
import { validateHLSFiles, editManifestFile } from '../../helpers/hlsVideo';

interface UploadState {
    name: string;
    description: string;
    stripePriceId: string;
    price: string;
    infoPageStyle: PackInfoStyle;
    secondaryTitle: string;
    demoZip: File | null;
    baseZip: File | null;
    manifest: File | null;
    videoFiles: File[];
    videoThumbnail: File | null;
    cover: File | null;
    demoTrack: File | null;
    fullWidthImage: File | null;
}

function SoundPackUpload() {
    const [formData, setFormData] = useState<UploadState>({
        name: '',
        description: '',
        stripePriceId: '',
        price: '',
        infoPageStyle: 'style1',
        secondaryTitle: '',
        demoZip: null,
        baseZip: null,
        manifest: null,
        videoFiles: [],
        videoThumbnail: null,
        cover: null,
        demoTrack: null,
        fullWidthImage: null
    });
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState('');
    const [uploadedPackId, setUploadedPackId] = useState<string | null>(null);
    const [hlsValidationError, setHlsValidationError] = useState<string | null>(null);
    const [hlsBitrateWarning, setHlsBitrateWarning] = useState<string | null>(null);
    const [thumbnailValidationError, setThumbnailValidationError] = useState<string | null>(null);
    // The teaser this upload releases ('' = none).
    const [replacingUpcomingId, setReplacingUpcomingId] = useState('');
    const [upcomingPacks, setUpcomingPacks] = useState<DatabaseUpcomingPack[]>([]);
    const { soundPacks } = useSoundPacks();

    useEffect(() => {
        const unsub = DatabaseUpcomingPackModel.subscribeUpcomingPacks(
            true,
            (next) => setUpcomingPacks([...next].sort(DatabaseUpcomingPackModel.sortByReleaseDateAsc)),
        );
        return () => unsub();
    }, []);

    // Uploading onto a released teaser would overwrite that pack's document.
    const unreleasedUpcomingPacks = upcomingPacks.filter(pack => !soundPacks.some(p => p.id === pack.id));
    const replacingUpcomingPack = unreleasedUpcomingPacks.find(pack => pack.id === replacingUpcomingId);

    // Picking a teaser fills in its titles (they usually match the pack's).
    const handleReplacingChange = (id: string) => {
        setReplacingUpcomingId(id);
        const pack = upcomingPacks.find(p => p.id === id);
        if (pack) {
            setFormData(prev => ({ ...prev, name: pack.name, secondaryTitle: pack.secondaryTitle ?? '' }));
        }
    };

    const handleFileChange = (field: keyof Omit<UploadState, 'name'>, file: File | null) => {
        setFormData(prev => ({ ...prev, [field]: file }));
    };

    const handleVideoThumbnailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;

        if (!file) {
            setFormData(prev => ({ ...prev, videoThumbnail: null }));
            setThumbnailValidationError(null);
            return;
        }

        // Validate file type
        if (file.type !== 'image/jpeg') {
            setThumbnailValidationError('File must be a JPG image');
            setFormData(prev => ({ ...prev, videoThumbnail: null }));
            return;
        }

        // Validate resolution
        const validation = await validateThumbnailResolution(file);

        if (!validation.valid) {
            setThumbnailValidationError(validation.error || 'Invalid thumbnail');
            setFormData(prev => ({ ...prev, videoThumbnail: null }));
        } else {
            setThumbnailValidationError(null);
            setFormData(prev => ({ ...prev, videoThumbnail: file }));
        }
    };

    const validateThumbnailResolution = (file: File): Promise<{ valid: boolean; error?: string }> => {
        return new Promise((resolve) => {
            const img = new Image();
            const url = URL.createObjectURL(file);

            img.onload = () => {
                URL.revokeObjectURL(url);

                // Check for 16:9 aspect ratio (1920x1080)
                if (img.width === 1920 && img.height === 1080) {
                    resolve({ valid: true });
                } else {
                    resolve({
                        valid: false,
                        error: `Resolution must be 1920x1080 (found ${img.width}x${img.height})`
                    });
                }
            };

            img.onerror = () => {
                URL.revokeObjectURL(url);
                resolve({ valid: false, error: 'Failed to load image' });
            };

            img.src = url;
        });
    };

    const handleVideoFolderChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            setFormData(prev => ({ ...prev, videoFiles: filesArray }));

            // Validate HLS files immediately
            const validation = await validateHLSFiles(filesArray);

            if (!validation.valid) {
                setHlsValidationError(validation.error || 'Invalid HLS files');
            } else {
                setHlsValidationError(null);
            }
            setHlsBitrateWarning(validation.bitrateWarning ?? null);
        }
    };

    const isFormValid = () => {
        // Sound content + cover are required for every pack; the info-page hero
        // media depends on the selected layout. Style 1 needs the HLS video +
        // thumbnail; style 2's full-width image is optional (can be added later
        // in the Edit Preview tab).
        const baseValid = formData.name.trim() !== '' &&
            formData.demoZip !== null &&
            formData.baseZip !== null &&
            formData.manifest !== null &&
            (formData.cover !== null || !!replacingUpcomingPack?.coverImageUrl);

        if (formData.infoPageStyle === 'style1') {
            return baseValid &&
                formData.videoFiles.length > 0 &&
                hlsValidationError === null &&
                formData.videoThumbnail !== null &&
                thumbnailValidationError === null;
        }

        return baseValid;
    };

    const handleUpload = async () => {
        if (!isFormValid()) {
            alert('Please fill in all fields');
            return;
        }

        setUploading(true);
        setProgress('Creating Firestore document...');
        setUploadedPackId(null); try {
            // Step 0: Parse + validate the sound list manifest before any writes,
            // so a malformed manifest aborts before we create a doc or upload zips.
            setProgress('Parsing sound list manifest...');
            const manifestText = await formData.manifest!.text();
            const manifestSounds: DatabaseSound[] =
                DatabaseSoundPackSoundsModel.soundsFromManifest(JSON.parse(manifestText));

            // Step 1: Create document in Firestore
            const packId = await DatabaseSoundPackModel.createSoundPack(formData.name, replacingUpcomingPack);
            setUploadedPackId(packId);
            setProgress(`Document created with ID: ${packId}`);

            // Step 1.1: Publish the sound list to sound_packs_sounds
            setProgress(`Publishing sound list (${manifestSounds.length} sounds)...`);
            await DatabaseSoundPackSoundsModel.setPackSounds(packId, manifestSounds);

            // Step 1.5: Set description if provided
            if (formData.description.trim()) {
                setProgress('Setting description...');
                await DatabaseSoundPackModel.updateDescription(packId, formData.description.trim());
            }

            // Step 1.6: Set Stripe Price ID if provided
            if (formData.stripePriceId.trim()) {
                setProgress('Setting Stripe Price ID...');
                await DatabaseSoundPackModel.updatePriceId(packId, formData.stripePriceId.trim());
            }

            // Step 1.7: Set price if provided
            if (formData.price.trim()) {
                setProgress('Setting price...');
                await DatabaseSoundPackModel.updatePrice(packId, formData.price.trim());
            }

            // Step 1.8: Set the info-page layout variant
            setProgress('Setting info page layout...');
            await DatabaseSoundPackModel.setInfoPageStyle(packId, formData.infoPageStyle);

            // Step 1.9: Set style2 secondary title if provided
            if (formData.secondaryTitle.trim()) {
                setProgress('Setting secondary title...');
                await DatabaseSoundPackModel.updateSecondaryTitle(packId, formData.secondaryTitle.trim());
            }

            // Step 2: Upload files to Storage and collect URLs
            setProgress('Uploading demo.zip (0%)...');
            const demoZipUrl = await DatabaseSoundPackModel.uploadFile(
                packId,
                formData.demoZip!,
                'demo.zip',
                (percent) => setProgress(`Uploading demo.zip (${percent}%)...`)
            );

            setProgress('Uploading base.zip (0%)...');
            const baseZipUrl = await DatabaseSoundPackModel.uploadFile(
                packId,
                formData.baseZip!,
                'base.zip',
                (percent) => setProgress(`Uploading base.zip (${percent}%)...`)
            );

            // A released teaser's cover is already this pack's cover.png.
            let coverImageUrl = replacingUpcomingPack?.coverImageUrl;
            if (!coverImageUrl) {
                setProgress('Uploading cover.png...');
                coverImageUrl = await DatabaseSoundPackModel.uploadFile(
                    packId,
                    formData.cover!,
                    'cover.png'
                );
            }

            // Step 2.5: Info-page hero media — only the assets the chosen layout
            // uses. Style 1 → HLS video + thumbnail (+ optional demo track);
            // style 2 → full-width image (optional).
            const mediaUrls: Parameters<typeof DatabaseSoundPackModel.updateMediaUrls>[1] = {
                demoZipUrl,
                baseZipUrl,
                coverImageUrl,
            };

            if (formData.infoPageStyle === 'style1') {
                setProgress('Editing manifest file...');
                const editedVideoFiles = await editManifestFile(formData.videoFiles, packId);

                setProgress(`Uploading HLS video files (${editedVideoFiles.length} files) (0%)...`);
                mediaUrls.videoPlaylistUrl = await DatabaseSoundPackModel.uploadHLSVideo(
                    packId,
                    editedVideoFiles,
                    (percent) => setProgress(`Uploading HLS video files (${editedVideoFiles.length} files) (${percent}%)...`)
                );

                setProgress('Uploading video thumbnail...');
                mediaUrls.videoThumbnailUrl = await DatabaseSoundPackModel.uploadFile(
                    packId,
                    formData.videoThumbnail!,
                    'video/thumbnail.jpg'
                );

                if (formData.demoTrack) {
                    const ext = formData.demoTrack.name.split('.').pop() || 'mp3';
                    setProgress(`Uploading demo track (0%)...`);
                    mediaUrls.demoTrackUrl = await DatabaseSoundPackModel.uploadFile(
                        packId,
                        formData.demoTrack,
                        `demo_track.${ext}`,
                        (percent) => setProgress(`Uploading demo track (${percent}%)...`)
                    );
                }
            } else if (formData.fullWidthImage) {
                setProgress('Uploading full-width image...');
                mediaUrls.fullWidthImageUrl = await DatabaseSoundPackModel.uploadFile(
                    packId,
                    formData.fullWidthImage,
                    'full_width.png',
                    (percent) => setProgress(`Uploading full-width image (${percent}%)...`)
                );
            }

            // Step 3: Cache media URLs in Firestore for faster loading
            setProgress('Caching media URLs...');
            await DatabaseSoundPackModel.updateMediaUrls(packId, mediaUrls);

            setProgress('✅ Upload complete!');
            alert(`Sound pack "${formData.name}" uploaded successfully!\nID: ${packId}`);

            // Reset form
            setReplacingUpcomingId('');
            setFormData({
                name: '',
                description: '',
                stripePriceId: '',
                price: '',
                infoPageStyle: 'style1',
                secondaryTitle: '',
                demoZip: null,
                baseZip: null,
                manifest: null,
                videoFiles: [],
                videoThumbnail: null,
                cover: null,
                demoTrack: null,
                fullWidthImage: null
            });

        } catch (error) {
            console.error('Upload error:', error);
            setProgress('❌ Upload failed');
            alert('Upload failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div>
            <div className="space-y-6">
                {/* Upcoming pack this upload releases */}
                {unreleasedUpcomingPacks.length > 0 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Replaces upcoming pack (optional)
                        </label>
                        <select
                            value={replacingUpcomingId}
                            onChange={(e) => handleReplacingChange(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                            disabled={uploading}
                        >
                            <option value="">None</option>
                            {unreleasedUpcomingPacks.map(pack => (
                                <option key={pack.id} value={pack.id}>
                                    {pack.name}{pack.secondaryTitle ? ` · ${pack.secondaryTitle}` : ''} (coming {formatShortDate(pack.releaseDate)}){pack.public ? '' : ' (private)'}
                                </option>
                            ))}
                        </select>
                        {replacingUpcomingPack && (
                            <p className="text-xs text-gray-500 mt-1">
                                The pack takes over the teaser's id and release date. The plugin hides the teaser once the pack is visible; delete it from the Upcoming tab whenever convenient.
                            </p>
                        )}
                    </div>
                )}

                {/* Sound Pack Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sound Pack Name *
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g. Lo-Fi Drums Vol. 1"
                        disabled={uploading}
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description (optional)
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="A brief description of the sound pack..."
                        rows={3}
                        disabled={uploading}
                    />
                </div>

                {/* Stripe Price ID */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Stripe Price ID (optional)
                    </label>
                    <input
                        type="text"
                        value={formData.stripePriceId}
                        onChange={(e) => setFormData(prev => ({ ...prev, stripePriceId: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g. price_1abc..."
                        disabled={uploading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        You can add this later in the Edit Preview tab
                    </p>
                </div>

                {/* Price */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price (in USD, without $) (optional)
                    </label>
                    <input
                        type="text"
                        value={formData.price}
                        onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="9.99"
                        disabled={uploading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Enter the price without the dollar sign (e.g., 9.99)
                    </p>
                </div>

                {/* Info Page Layout */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Info Page Layout
                    </label>
                    <select
                        value={formData.infoPageStyle}
                        onChange={(e) => setFormData(prev => ({ ...prev, infoPageStyle: e.target.value as PackInfoStyle }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        disabled={uploading}
                    >
                        <option value="style1">Style 1 (video + demo track)</option>
                        <option value="style2">Style 2 (full-width image)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                        Determines which media the pack's info page shows below.
                    </p>
                </div>

                {/* Secondary Title (header subtitle, both layouts) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Secondary Title (optional)
                    </label>
                    <input
                        type="text"
                        value={formData.secondaryTitle}
                        onChange={(e) => setFormData(prev => ({ ...prev, secondaryTitle: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g. Vol 1"
                        disabled={uploading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Shown under the pack name on the info page header.
                    </p>
                </div>

                {/* Style 2: Full-Width Image */}
                {formData.infoPageStyle === 'style2' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Full-Width Image (optional)
                        </label>
                        <input
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={(e) => handleFileChange('fullWidthImage', e.target.files?.[0] || null)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                            disabled={uploading}
                        />
                        {formData.fullWidthImage && (
                            <div className="mt-2">
                                <p className="text-sm text-gray-500 mb-2">
                                    Selected: {formData.fullWidthImage.name} ({(formData.fullWidthImage.size / 1024 / 1024).toFixed(2)} MB)
                                </p>
                                <img
                                    src={URL.createObjectURL(formData.fullWidthImage)}
                                    alt="Full-width preview"
                                    className="w-full max-w-md rounded object-cover"
                                />
                            </div>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            Can be added later in the Edit Preview tab.
                        </p>
                    </div>
                )}

                {/* Demo ZIP File */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Demo ZIP File (demo.zip) *
                    </label>
                    <input
                        type="file"
                        accept=".zip"
                        onChange={(e) => handleFileChange('demoZip', e.target.files?.[0] || null)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        disabled={uploading}
                    />
                    {formData.demoZip && (
                        <p className="text-sm text-gray-500 mt-1">
                            Selected: {formData.demoZip.name} ({(formData.demoZip.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                    )}
                </div>

                {/* Full Pack ZIP File */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Pack ZIP File (base.zip) *
                    </label>
                    <input
                        type="file"
                        accept=".zip"
                        onChange={(e) => handleFileChange('baseZip', e.target.files?.[0] || null)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        disabled={uploading}
                    />
                    {formData.baseZip && (
                        <p className="text-sm text-gray-500 mt-1">
                            Selected: {formData.baseZip.name} ({(formData.baseZip.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                    )}
                </div>

                {/* Sound list manifest */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sound List Manifest (pack_manifest.json) *
                    </label>
                    <input
                        type="file"
                        accept=".json,application/json"
                        onChange={(e) => handleFileChange('manifest', e.target.files?.[0] || null)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        disabled={uploading}
                    />
                    {formData.manifest && (
                        <p className="text-sm text-gray-500 mt-1">
                            Selected: {formData.manifest.name} ({(formData.manifest.size / 1024).toFixed(1)} KB)
                        </p>
                    )}
                </div>

                {/* Style 1 hero media: HLS video + thumbnail */}
                {formData.infoPageStyle === 'style1' && (
                <>
                {/* HLS Video Folder */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        HLS Video Folder (.m3u8 + .ts segments) *
                    </label>
                    <input
                        type="file"
                        {...({ webkitdirectory: '', directory: '' } as any)}
                        multiple
                        onChange={handleVideoFolderChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        disabled={uploading}
                    />
                    {formData.videoFiles.length > 0 && (
                        <div className="text-sm text-gray-500 mt-1">
                            <p className="font-medium">{formData.videoFiles.length} files selected</p>
                            {hlsValidationError && (
                                <p className="text-red-600 font-medium mt-1">⚠️ {hlsValidationError}</p>
                            )}
                            {!hlsValidationError && (
                                <p className="text-green-600 font-medium mt-1">✓ Valid HLS folder</p>
                            )}
                            {!hlsValidationError && hlsBitrateWarning && (
                                <p className="text-amber-600 font-medium mt-1">⚠️ {hlsBitrateWarning}</p>
                            )}
                            <div className="mt-1 max-h-32 overflow-y-auto bg-gray-50 rounded p-2">
                                {formData.videoFiles.map((file, index) => (
                                    <div key={index} className="text-xs py-0.5">
                                        • {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Video Thumbnail */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Video Thumbnail (1920x1080 JPG) *
                    </label>
                    <input
                        type="file"
                        accept="image/jpeg"
                        onChange={handleVideoThumbnailChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        disabled={uploading}
                    />
                    {formData.videoThumbnail && (
                        <div className="mt-2">
                            <p className="text-sm text-gray-500 mb-2">
                                Selected: {formData.videoThumbnail.name} ({(formData.videoThumbnail.size / 1024 / 1024).toFixed(2)} MB)
                            </p>
                            {thumbnailValidationError && (
                                <p className="text-red-600 font-medium text-sm mt-1">⚠️ {thumbnailValidationError}</p>
                            )}
                            {!thumbnailValidationError && (
                                <p className="text-green-600 font-medium text-sm mt-1">✓ Valid 1920x1080 JPG</p>
                            )}
                            <img
                                src={URL.createObjectURL(formData.videoThumbnail)}
                                alt="Thumbnail preview"
                                className="w-64 h-36 object-cover rounded mt-2"
                            />
                        </div>
                    )}
                    {thumbnailValidationError && !formData.videoThumbnail && (
                        <p className="text-red-600 font-medium text-sm mt-1">⚠️ {thumbnailValidationError}</p>
                    )}
                </div>
                </>
                )}

                {/* Cover Image */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cover Image (cover.png) *
                    </label>
                    {replacingUpcomingPack?.coverImageUrl ? (
                        <div>
                            <p className="text-sm text-gray-500 mb-2">
                                From the upcoming pack — already stored as this pack's cover and cached by the plugin, so it can't be changed here.
                            </p>
                            <img
                                src={replacingUpcomingPack.coverImageUrl}
                                alt="Cover preview"
                                className="w-32 h-32 object-cover rounded"
                            />
                        </div>
                    ) : (
                        <>
                            <input
                                type="file"
                                accept="image/png,image/jpeg"
                                onChange={(e) => handleFileChange('cover', e.target.files?.[0] || null)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                                disabled={uploading}
                            />
                            {formData.cover && (
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500 mb-2">
                                        Selected: {formData.cover.name} ({(formData.cover.size / 1024 / 1024).toFixed(2)} MB)
                                    </p>
                                    <img
                                        src={URL.createObjectURL(formData.cover)}
                                        alt="Cover preview"
                                        className="w-32 h-32 object-cover rounded"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Demo Track (optional, style 1 only) */}
                {formData.infoPageStyle === 'style1' && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Demo Track (optional audio file)
                    </label>
                    <input
                        type="file"
                        accept=".mp3,.wav,.m4a,.aac,.flac"
                        onChange={(e) => handleFileChange('demoTrack', e.target.files?.[0] || null)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md"
                        disabled={uploading}
                    />
                    {formData.demoTrack && (
                        <p className="text-sm text-gray-500 mt-1">
                            Selected: {formData.demoTrack.name} ({(formData.demoTrack.size / 1024 / 1024).toFixed(2)} MB)
                        </p>
                    )}
                </div>
                )}

                {/* Upload Button */}
                <button
                    onClick={handleUpload}
                    disabled={!isFormValid() || uploading}
                    className={`w-full py-3 px-4 rounded-md text-white font-medium ${isFormValid() && !uploading
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-gray-400 cursor-not-allowed'
                        }`}
                >
                    {uploading ? 'Uploading...' : 'Upload to preview'}
                </button>

                {/* Progress Display */}
                {progress && (
                    <div className={`p-4 rounded-md ${progress.startsWith('✅') ? 'bg-green-100 text-green-800' :
                        progress.startsWith('❌') ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                        }`}>
                        {progress}
                    </div>
                )}

                {/* Success Message with ID */}
                {uploadedPackId && !uploading && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                        <p className="text-sm text-green-800">
                            <strong>Sound Pack ID:</strong> <code className="bg-green-100 px-2 py-1 rounded">{uploadedPackId}</code>
                        </p>
                        <p className="text-xs text-green-600 mt-2">
                            You can now link a Stripe price ID in the "Link Stripe Price" tab.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SoundPackUpload;
